// The seat page (`/c/{slug}/chart/seat/{id}`; spec 06 §Main screens 8; Chart UX pass
// 2026-10-21): one seat through the Chart lens, laid out like a profile. The 56 px location
// header carries the seat's title and its actions (Focus, Open the DE, Lines, Edit seat — Setup's
// seat drawer, opened here; UX-A 1.7); under
// it the profile: the holder's avatar and name, the seat, its role, its teams (managers add and
// remove here, 04 `teams.addMember` / `removeMember`), who it reports to, who reports to it, and
// its authority and delegation lines both ways. Then the tabs: Responsibilities (M1-05, with the
// recurring work under each), Recurring work (every schedule of the seat in one table), KPIs
// (KPI-ASSIGN's SeatKpisPanel: "KPIs for this seat (held by …)", each KPI's health for the focused
// week, recent values and source, and the one-step KPI editor for managers; `kpi`), Money (the seat's ledger for that month and its
// budget from budget vs actual, M5-03).
// On load: the org chart (shared with the sidebar), the roles, and the open tab's queries.
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  InlineBar,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  StatGrid,
  StatTile,
  TabsContent,
  ViewTabs,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArmchairIcon,
  BotIcon,
  CrosshairIcon,
  GitBranchPlusIcon,
  PencilIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus, useFocus } from "../../focus/focus";
import { NotEnabledPage, NotFoundPage } from "../../pages/status-pages";
import { SeatRoutines } from "../automations/routines";
import { type CompanyAccess, isManager } from "../company";
import { CostTab, monthOfWeek } from "../de/cost-tab";
import { budgetState } from "../finance/budget-vs-actual";
import { monthLabel, PayHiddenBadge, ratioText } from "../finance/finance-parts";
import { budgetVsActualQuery } from "../finance/finance-queries";
import { formatMoney } from "../format";
import { holdersOf, SeatKpisPanel } from "../kpi/seat-kpis";
import { useReachConfirm } from "../memory/reach-confirm";
import { rolesQuery } from "../queries";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import { SEAT_STATUS_LABELS } from "../structure/labels";
import { FormError } from "../structure/parts";
import { SeatResponsibilities } from "../structure/responsibilities";
import { SEAT_STATUS_TONE } from "../structure/seats-screen";
import { useFeature } from "../use-feature";
import {
  type ChartSeat,
  COMPOSITION_LABELS,
  LINE_VERBS,
  type OrgChart,
  seatComposition,
} from "./chart-model";
import {
  ChartBody,
  ChartFrame,
  chartCrumb,
  HolderNamesBadged,
  holderKind,
  SeatAvatar,
  seatNameText,
} from "./chart-parts";
import { chartKeys, orgChartQuery } from "./chart-queries";
import { EditSeatDrawer } from "./edit-seat-drawer";

export const SEAT_TABS = ["responsibilities", "recurring", "kpis", "money"] as const;
export type SeatTab = (typeof SEAT_TABS)[number];
const TAB_LABELS: Record<SeatTab, string> = {
  responsibilities: "Responsibilities",
  recurring: "Recurring work",
  kpis: "KPIs",
  money: "Money",
};
/** Older links (`?tab=overview|cost`, M1-08) land on the tab that holds what they named. */
export const SEAT_TAB_ALIASES: Record<string, SeatTab> = {
  overview: "responsibilities",
  cost: "money",
};

export function SeatPage({
  api,
  company,
  id,
  tab = "responsibilities",
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  tab?: SeatTab;
  onTab: (tab: SeatTab) => void;
}) {
  const kpi = useFeature("kpi");
  const manager = isManager(company);
  const week = usePeriod();
  const { focus, setFocus } = useFocus();
  const chartQ = useQuery(orgChartQuery(api, company.id));
  const roles = useInfiniteQuery(rolesQuery(api, company.id));
  // UX-A 1.7: "Edit seat" opens Setup's seat drawer here, over the seat page.
  const [editing, setEditing] = useState(false);
  const chart = chartQ.data;
  const seat = chart?.seats.find((s) => s.id === id);
  if (chart && !seat) return <NotFoundPage message="No such seat in this company's chart." />;

  const role = roles.data?.pages.flatMap((p) => p.data).find((r) => r.id === seat?.role_id);
  const composition = seat ? seatComposition(seat) : null;
  const de = seat?.holders.find((h) => h.membership_kind === "digital_employee");
  const focused = focus.subject?.kind === "seat" && focus.subject.id === id;
  const tabs = SEAT_TABS.filter((t) => t !== "kpis" || kpi.enabled);

  // Phones: the actions are icons (their names stay for screen readers) and the badges go, so
  // the title keeps its room.
  return (
    <ChartFrame data-screen="seat">
      <LocationHeader
        crumbs={[
          chartCrumb(company),
          {
            label: "Org chart",
            render: (
              <Link to="/c/$slug/kpis/chart" params={{ slug: company.slug }} search={keepFocus} />
            ),
          },
        ]}
        icon={ArmchairIcon}
        title={seat?.title ?? "Seat"}
        badge={
          seat && composition ? (
            <span className="flex gap-1.5 max-md:hidden">
              {composition === "open" ? null : (
                <Badge
                  variant={composition === "human" ? "neutral" : "accent"}
                  data-composition={composition}
                >
                  {COMPOSITION_LABELS[composition]}
                </Badge>
              )}
              {seat.status !== "filled" ? (
                <Badge variant={SEAT_STATUS_TONE[seat.status]}>
                  {SEAT_STATUS_LABELS[seat.status]}
                </Badge>
              ) : null}
            </span>
          ) : null
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={focused}
              className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
              onClick={() => setFocus({ subject: focused ? null : { kind: "seat", id } })}
            >
              <CrosshairIcon aria-hidden="true" />
              <span className="max-md:sr-only">{focused ? "Focused" : "Focus"}</span>
            </Button>
            {de ? (
              <Link
                to="/c/$slug/agents/de/$id"
                params={{ slug: company.slug, id: de.membership_id }}
                search={keepFocus}
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              >
                <BotIcon aria-hidden="true" />
                <span className="max-md:sr-only">Open {workerName(de.display_name)}</span>
              </Link>
            ) : null}
            {manager && seat ? (
              <>
                <Link
                  to="/c/$slug/kpis/chart"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  aria-label={`Lines of ${seat.title}: in the chart`}
                  title="Reports to, approves for, hands work to: drawn in the chart"
                  className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
                >
                  <GitBranchPlusIcon aria-hidden="true" />
                  <span className="max-md:sr-only">Lines</span>
                </Link>
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                  <PencilIcon aria-hidden="true" />
                  <span className="max-md:sr-only">Edit seat</span>
                </Button>
              </>
            ) : null}
          </>
        }
      />
      {editing && manager ? (
        <EditSeatDrawer
          api={api}
          company={company}
          seatId={id}
          roles={roles.data?.pages.flatMap((p) => p.data) ?? []}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {chartQ.isPending ? (
        <ChartBody>
          <Loading label="Loading the seat" rows={4} />
        </ChartBody>
      ) : chartQ.isError ? (
        <ChartBody>
          <LoadError what="The seat" error={chartQ.error} />
        </ChartBody>
      ) : chart && seat ? (
        <>
          <Profile api={api} company={company} chart={chart} seat={seat} role={role?.name} />
          <ViewTabs
            className="min-w-0 px-6 max-md:px-4"
            aria-label="Seat sections"
            value={tab}
            onValueChange={(v) => onTab(v as SeatTab)}
            tabs={tabs.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
          >
            <TabsContent value="responsibilities" className="min-w-0 py-4">
              <SeatResponsibilities
                api={api}
                company={company}
                seatId={id}
                readOnly={!manager || seat.status === "closed"}
              />
            </TabsContent>
            <TabsContent value="recurring" className="min-w-0 py-4">
              {/* Parity P5: the routines with their controls (automations/routines.tsx) */}
              <SeatRoutines api={api} company={company} seatId={id} readOnly={!manager} />
            </TabsContent>
            {kpi.enabled ? (
              <TabsContent value="kpis" className="min-w-0 py-4">
                {/* KPI-ASSIGN: the shared KPIs panel (health, recent values, source, the
                    one-step KPI editor for managers). */}
                <SeatKpisPanel
                  api={api}
                  company={company}
                  seatId={id}
                  seatTitle={seat.title}
                  holders={holdersOf(seat)}
                  week={week}
                  readOnly={seat.status === "closed"}
                />
              </TabsContent>
            ) : (
              <TabsContent value="kpis" className="min-w-0 py-4">
                <NotEnabledPage
                  what="Seat KPIs"
                  feature="kpi"
                  companyName={company.name}
                  slug={company.slug}
                />
              </TabsContent>
            )}
            <TabsContent value="money" className="min-w-0 py-4">
              <div className="flex min-w-0 flex-col gap-4">
                <SeatBudget api={api} company={company} seatId={id} week={week} />
                <CostTab api={api} company={company} seatId={id} week={week} />
              </div>
            </TabsContent>
          </ViewTabs>
        </>
      ) : null}
    </ChartFrame>
  );
}

/** The seat's month against its budget (budget vs actual, the month of the focused week). */
function SeatBudget({
  api,
  company,
  seatId,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  week: string;
}) {
  const month = monthOfWeek(week);
  const view = useQuery(budgetVsActualQuery(api, company.id, "month", month));
  const node = view.data?.nodes.find((n) => n.kind === "seat" && n.id === seatId);
  const currency = view.data?.currency ?? "USD";
  const money = (minor: number | null) => (minor === null ? "—" : formatMoney(minor, currency));
  if (view.isPending) return <Loading label="Loading the budget" rows={1} />;
  if (view.isError) return <LoadError what="The budget" error={view.error} />;
  if (!node)
    return (
      <p className="text-xs text-muted-foreground" data-slot="seat-budget">
        {monthLabel(month)}: this seat's budget and spend are not yours to read.
      </p>
    );
  const state = budgetState(node);
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot="seat-budget">
      <StatGrid dense aria-label={`${monthLabel(month)} budget`}>
        <StatTile
          size="sm"
          label="Budget"
          value={money(node.budget_minor)}
          detail={monthLabel(month)}
        />
        <StatTile
          size="sm"
          label="Actual"
          value={money(node.actual_minor)}
          detail={node.restricted_hidden ? <PayHiddenBadge /> : "Pay included"}
        />
        <StatTile
          size="sm"
          label={(node.variance_minor ?? 0) < 0 ? "Over by" : "Left"}
          value={node.variance_minor === null ? "—" : money(Math.abs(node.variance_minor))}
          detail={state.label}
        />
        <StatTile
          size="sm"
          label="Budget used"
          value={ratioText(node.utilization)}
          detail={
            node.utilization === null ? (
              "No budget set"
            ) : (
              <InlineBar
                value={Number(node.utilization) * 100}
                size="xs"
                className="mt-1"
                tone={
                  state.tone === "danger"
                    ? "danger"
                    : state.tone === "warning"
                      ? "warning"
                      : "primary"
                }
                aria-hidden="true"
              />
            )
          }
        />
      </StatGrid>
      <Link
        to="/c/$slug/kpis/reports/finance"
        params={{ slug: company.slug }}
        search={keepFocus}
        className="self-start text-xs text-primary-text underline-offset-4 hover:underline"
      >
        Budget vs actual by team
      </Link>
    </div>
  );
}

/** The profile under the header: avatar, name, seat facts, teams, lines. */
function Profile({
  api,
  company,
  chart,
  seat,
  role,
}: {
  api: Api;
  company: CompanyAccess;
  chart: OrgChart;
  seat: ChartSeat;
  role: string | undefined;
}) {
  const titleOf = (sid: string) => chart.seats.find((s) => s.id === sid)?.title ?? "A seat";
  const seatLink = (sid: string) => {
    const s = chart.seats.find((x) => x.id === sid);
    return (
      <Link
        key={sid}
        to="/c/$slug/kpis/chart/seat/$id"
        params={{ slug: company.slug, id: sid }}
        search={keepFocus}
        className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline"
        title={s ? `${seatNameText(s)} · ${s.title}` : undefined}
      >
        {s?.holders.length ? (
          <>
            <SeatAvatar seat={s} size="xs" />
            <HolderNamesBadged seat={s} />
            <span className="font-normal text-muted-foreground">· {s.title}</span>
          </>
        ) : (
          titleOf(sid)
        )}
      </Link>
    );
  };
  const reports = chart.seats
    .filter((s) => s.supervisor_seat_id === seat.id && s.status !== "closed")
    .sort((a, b) => a.title.localeCompare(b.title))
    .map((s) => s.id);
  const line = (kind: "authority" | "delegation", dir: "out" | "in") =>
    chart.edges
      .filter((e) => e.kind === kind && (dir === "out" ? e.from_seat_id : e.to_seat_id) === seat.id)
      .map((e) => (dir === "out" ? e.to_seat_id : e.from_seat_id));
  const fact = (label: string, value: ReactNode) => (
    <div className="flex min-w-0 items-baseline gap-2 text-sm">
      <dt className="w-28 shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">{value}</dd>
    </div>
  );
  const list = (ids: string[]) =>
    ids.length ? ids.map(seatLink) : <span className="text-muted-foreground">None</span>;

  return (
    <ChartBody className="flex-none gap-3 border-b border-subtle" data-slot="seat-profile">
      <div className="flex min-w-0 items-start gap-4">
        <SeatAvatar seat={seat} size="lg" className="size-14 rounded-lg text-xl [&_svg]:size-7" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className="font-heading text-xl leading-7 font-semibold tracking-heading text-foreground"
            data-slot="seat-holder-name"
          >
            {seat.holders.length ? <HolderNamesBadged seat={seat} /> : seatNameText(seat)}
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
            <span>{seat.title}</span>
            <span aria-hidden="true">·</span>
            <span>{role ?? "Role"}</span>
            {seat.holders.length > 1 || seat.holders.some((h) => Number(h.share) !== 1) ? (
              <>
                <span aria-hidden="true">·</span>
                <span data-slot="seat-holders">
                  {seat.holders
                    .map(
                      (h) =>
                        `${workerName(h.display_name)}${
                          Number(h.share) !== 1 ? ` ${Math.round(Number(h.share) * 100)}%` : ""
                        }`,
                    )
                    .join(", ")}
                </span>
              </>
            ) : null}
          </span>
          <ul className="sr-only" data-slot="seat-holder-list">
            {seat.holders.map((h) => (
              <li key={h.membership_id} data-holder={h.membership_kind}>
                {workerName(h.display_name)} (
                {holderKind(h) === "agent" ? "digital employee" : "person"})
              </li>
            ))}
          </ul>
        </div>
      </div>
      <dl className="flex min-w-0 flex-col gap-1.5">
        {fact(
          "Reports to",
          seat.supervisor_seat_id ? (
            seatLink(seat.supervisor_seat_id)
          ) : (
            <span className="text-muted-foreground">Nobody (top of the chart)</span>
          ),
        )}
        {fact(`Direct reports (${reports.length})`, list(reports))}
        {fact("Teams", <SeatTeams api={api} company={company} chart={chart} seat={seat} />)}
        {line("authority", "out").length
          ? fact(LINE_VERBS.authority, list(line("authority", "out")))
          : null}
        {line("authority", "in").length ? fact("Approved by", list(line("authority", "in"))) : null}
        {line("delegation", "out").length
          ? fact(LINE_VERBS.delegation, list(line("delegation", "out")))
          : null}
        {line("delegation", "in").length
          ? fact("Gets work from", list(line("delegation", "in")))
          : null}
      </dl>
    </ChartBody>
  );
}

function SeatTeams({
  api,
  company,
  chart,
  seat,
}: {
  api: Api;
  company: CompanyAccess;
  chart: OrgChart;
  seat: ChartSeat;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const manager = isManager(company);
  const [pick, setPick] = useState("");
  const mine = new Set(
    chart.team_members.filter((m) => m.seat_id === seat.id).map((m) => m.team_id),
  );
  const teams = chart.teams.filter((t) => mine.has(t.id));
  const others = chart.teams
    .filter((t) => !mine.has(t.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
    void queryClient.invalidateQueries({ queryKey: chartKeys.teams(company.id) });
  };
  // D8: an agent on this seat is first shown what it will read in memory after the move
  const { confirmReach, reachDialog } = useReachConfirm(api, company.id);
  const add = useMutation({
    mutationFn: async (team: string) => {
      const change = { kind: "seat_team", seat_id: seat.id, add_team_id: team } as const;
      if (!(await confirmReach({ ...change, remove_team_id: null }))) return null;
      return api.client.teams.addMember({
        params: { co: company.id, id: team },
        body: { seat_id: seat.id },
      });
    },
    onSuccess: () => {
      setPick("");
      done();
    },
  });
  const remove = useMutation({
    mutationFn: async (team: string) => {
      if (
        !(await confirmReach({
          kind: "seat_team",
          seat_id: seat.id,
          add_team_id: null,
          remove_team_id: team,
        }))
      )
        return null;
      return api.client.teams.removeMember({
        params: { co: company.id, id: team, seat_id: seat.id },
      });
    },
    onSuccess: done,
  });
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5" data-slot="seat-teams">
      {reachDialog}
      {teams.length ? (
        teams.map((t) => (
          <Badge key={t.id} variant="outline" className="h-6 gap-1 pr-1">
            <Link
              to="/c/$slug/kpis/chart"
              params={{ slug: company.slug }}
              search={(prev: Record<string, unknown>) => ({
                ...keepFocus(prev),
                scope: `team:${t.id}`,
              })}
              className="underline-offset-4 hover:underline"
            >
              {t.name}
            </Link>
            {manager ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove from ${t.name}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(t.id)}
              >
                <XIcon aria-hidden="true" />
              </Button>
            ) : null}
          </Badge>
        ))
      ) : (
        <span className="text-muted-foreground">Not on a team</span>
      )}
      {manager && others.length ? (
        <span className="flex items-center gap-1">
          <label htmlFor={`${id}-team`} className="sr-only">
            Add to a team
          </label>
          <NativeSelect
            id={`${id}-team`}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="h-7 text-xs"
          >
            <NativeSelectOption value="">Add to a team…</NativeSelectOption>
            {others.map((t) => (
              <NativeSelectOption key={t.id} value={t.id}>
                {t.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Add"
            disabled={!pick || add.isPending}
            onClick={() => add.mutate(pick)}
          >
            <PlusIcon aria-hidden="true" />
          </Button>
        </span>
      ) : null}
      <FormError what="Adding the seat to the team" error={add.error} />
      <FormError what="Removing the seat from the team" error={remove.error} />
    </span>
  );
}
